import { Box, CircularProgress, useTheme } from '@mui/material';
import { useGetPatientById, useUpdateGuardian, useUpdatePatient } from '_api';
import { PatientPayload, UpdateGuardianPayload } from '_api/types';
import { ACCOUNT_TYPE, AdminRouteMap, PatientRouteMap } from '_constants';
import { SEVERITY } from '_constants/derived';
import { FormHeaderText, TextButton } from 'components/atoms';
import { AlertMessage } from 'components/molecules';
import { EditPatientForm } from 'components/organisms/forms';
import React, { useEffect, useMemo, useState } from 'react';
import { IoArrowBackOutline } from 'react-icons/io5';
import { useParams } from 'react-router-dom';
import { useNavigateTo } from 'utils';
import { usePatients, useUser } from 'utils/hooks';

export const EditPatient = () => {
  const navigate = useNavigateTo();
  const theme = useTheme();
  const { patientId: id } = useParams();
  const patientId = useMemo(() => (id !== undefined ? Number(id) : 0), [id]);
  const { therapistHospitalPatientMRNs } = usePatients();
  const { account } = useUser();
  const { data: apiPatient, isLoading } = useGetPatientById(patientId);
  const { mutateAsync: updatePatient } = useUpdatePatient();
  const { mutateAsync: updateGuardian } = useUpdateGuardian();

  const [patient, setPatient] = useState(apiPatient);

  useEffect(() => {
    if (id) setPatient(apiPatient);
  }, [apiPatient, id]);

  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const onBack = () => {
    navigate(AdminRouteMap.PATIENTS);
  };

  const onSubmit = async (
    updatedPatient: PatientPayload,
    updatedGuardian: UpdateGuardianPayload,
  ) => {
    if (
      (account?.accountType === ACCOUNT_TYPE.THERAPIST ||
        account?.accountType === ACCOUNT_TYPE.HOS_ADMIN) &&
      updatedPatient.mrn !== undefined &&
      patient?.mrn !== updatedPatient.mrn &&
      therapistHospitalPatientMRNs.includes(updatedPatient.mrn)
    ) {
      setErrorMessage(`Patient with MRN: ${updatedPatient.mrn} already exists.`);
    } else {
      const patientUpdatePromise = updatePatient(updatedPatient);
      const guardianUpdatePromise = updateGuardian(updatedGuardian);

      try {
        await Promise.all([patientUpdatePromise, guardianUpdatePromise]);
        const { id } = updatedPatient;
        setShowSuccess(true);
        if (id) {
          setTimeout(() => {
            navigate(PatientRouteMap.DETAIL, id.toString());
          }, 1500);
        }
      } catch (error: any) {
        if (error.response && error.response.status === 403) {
          setErrorMessage(
            'Unexpected error occured. Please refresh the page and try again.',
          );
        } else if (error.message) {
          setErrorMessage(`ERROR: ${error.message}`);
        } else {
          setErrorMessage('Unexpected error. Please Try again.');
        }
      }
    }
  };

  return (
    <Box>
      <Box>
        <FormHeaderText gutterBottom> Child Detail </FormHeaderText>
        <TextButton
          onClick={onBack}
          sx={{ color: theme.palette.primary.main, marginBottom: '10px', paddingLeft: 0 }}
        >
          <IoArrowBackOutline />
          BACK
        </TextButton>
      </Box>
      {!isLoading && patient && patient.id === patientId ? (
        <Box>
          <EditPatientForm
            initValue={patient}
            disableAll={showSuccess}
            onSubmit={onSubmit}
            onBack={onBack}
          />
        </Box>
      ) : (
        <CircularProgress />
      )}
      <AlertMessage
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        message={'Patient sucessfully updated.'}
      />
      <AlertMessage
        open={!!errorMessage}
        onClose={() => setErrorMessage('')}
        severity={SEVERITY.error}
        message={errorMessage}
      />
    </Box>
  );
};
